import React, { useState } from "react";
import { api } from "../lib/api";

const LOOKS = [
  { slug: "bridal", title: "Bridal", caption: "Soft, glowing, your skin but better." },
  { slug: "smoky-eye", title: "Smoky Eye", caption: "Lived-in, smoldering, defined." },
  { slug: "soft-glam", title: "Soft Glam", caption: "Sun-kissed dimension with a glossy lip." },
  { slug: "editorial", title: "Editorial", caption: "Magazine-cover, bold, unexpected." },
  { slug: "bronze-goddess", title: "Bronze Goddess", caption: "Warm metallics and sculpted light." },
  { slug: "red-carpet", title: "Red Carpet", caption: "Full glam, every angle." },
];

const SERVICES = [
  {
    id: "bridal",
    title: "Bridal",
    blurb: "A bespoke look built around the dress, the venue, and how you want to feel walking down the aisle. Includes a private trial and day-of touch-ups.",
    starting: 450,
  },
  {
    id: "event",
    title: "Special Event",
    blurb: "Galas, milestone birthdays, anniversaries. One artist, one chair, one focused hour — designed for great photos and even better lighting.",
    starting: 220,
  },
  {
    id: "photoshoot",
    title: "Photoshoot / Editorial",
    blurb: "Camera-aware makeup that holds under hot lights and reads correctly through any lens. On-set throughout the shoot.",
    starting: 350,
  },
  {
    id: "lesson",
    title: "Private Lesson",
    blurb: "One-on-one studio session: we work through your bag, your face, your goals. You leave with a written routine and a custom edit.",
    starting: 180,
  },
];

export default function Artistry() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    service_type: "bridal",
    event_date: "",
    location: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/artistry/inquiry", form);
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't send your inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="artistry-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-5">Volume 02 · Studio Services</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] text-stone-900 tracking-tight">
              Makeup Artistry.
              <br />
              <em className="italic text-stone-700">By appointment.</em>
            </h1>
            <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-stone-600 font-light">
              One-on-one makeup for brides, photo days, and the kind of evenings that deserve a great brush in someone
              else&apos;s hand. Quiet, considered, camera-ready.
            </p>
            <a
              href="#inquire"
              data-testid="artistry-cta-inquire"
              className="mt-10 inline-block text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-7 py-4 transition-colors"
            >
              Book a Consultation
            </a>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden bg-[#F2EFEB]">
              <img src="/images/looks/bridal.png" alt="Bridal makeup look" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <div className="bb-divider" />

      {/* Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <div className="mb-14">
          <div className="overline mb-4">The Services</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-stone-900 font-light leading-tight">
            What we do.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {SERVICES.map((s, i) => (
            <div key={s.id} data-testid={`service-${s.id}`} className="border-t border-[#E5E0D8] pt-6">
              <div className="overline mb-3">{(i + 1).toString().padStart(2, "0")}</div>
              <h3 className="font-serif-display text-3xl text-stone-900 leading-tight">{s.title}</h3>
              <p className="mt-4 text-stone-600 leading-relaxed">{s.blurb}</p>
              <div className="mt-5 flex items-baseline justify-between">
                <span className="text-sm text-stone-500">
                  Starting at <span className="text-stone-900 font-medium">${s.starting}</span>
                </span>
                <a
                  href="#inquire"
                  onClick={() => setForm((f) => ({ ...f, service_type: s.id }))}
                  className="text-xs uppercase tracking-[0.22em] text-[#8C6A5D] hover:text-[#70544A]"
                  data-testid={`service-cta-${s.id}`}
                >
                  Inquire →
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Look Book */}
      <section className="bg-[#F2EFEB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <div className="mb-14">
            <div className="overline mb-4">The Look Book</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-stone-900 font-light leading-tight">
              A few of our looks.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="lookbook-grid">
            {LOOKS.map((l, i) => (
              <figure key={l.slug} data-testid={`look-${l.slug}`} className="bg-white border border-[#E5E0D8] bb-card-hover">
                <div className="aspect-[4/5] overflow-hidden bg-[#F2EFEB]">
                  <img
                    src={`/images/looks/${l.slug}.png`}
                    alt={`${l.title} makeup look`}
                    className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-700"
                  />
                </div>
                <figcaption className="p-5">
                  <div className="overline mb-1">{(i + 1).toString().padStart(2, "0")}</div>
                  <div className="font-serif-display text-2xl text-stone-900">{l.title}</div>
                  <p className="text-sm text-stone-500 italic mt-1">{l.caption}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry */}
      <section id="inquire" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <div className="mb-12 text-center">
          <div className="overline mb-4">Book a Consultation</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-stone-900 font-light leading-tight">
            Tell us about your day.
          </h2>
          <p className="mt-4 text-stone-600 max-w-xl mx-auto">
            A few details and we&apos;ll be in touch within 48 hours with availability and a custom proposal.
          </p>
        </div>

        {success ? (
          <div data-testid="artistry-success" className="bg-white border border-[#E5E0D8] p-10 text-center max-w-2xl mx-auto">
            <div className="overline mb-3">Inquiry Received</div>
            <h3 className="font-serif-display text-3xl text-stone-900 leading-tight">
              Thank you. We&apos;ll be in touch.
            </h3>
            <p className="mt-4 text-stone-600">
              Your inquiry is in our studio. You&apos;ll hear back from us within 48 hours — usually much sooner.
            </p>
          </div>
        ) : (
          <form data-testid="artistry-form" onSubmit={submit} className="bg-white border border-[#E5E0D8] p-8 lg:p-10 max-w-2xl mx-auto space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="ar-name" className="overline block mb-2">Name</label>
                <input id="ar-name" data-testid="ar-name" type="text" required value={form.name} onChange={onChange("name")}
                  className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3" />
              </div>
              <div>
                <label htmlFor="ar-email" className="overline block mb-2">Email</label>
                <input id="ar-email" data-testid="ar-email" type="email" required value={form.email} onChange={onChange("email")}
                  className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="ar-service" className="overline block mb-2">Service</label>
                <select id="ar-service" data-testid="ar-service" value={form.service_type} onChange={onChange("service_type")}
                  className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3">
                  <option value="bridal">Bridal</option>
                  <option value="event">Special Event</option>
                  <option value="photoshoot">Photoshoot / Editorial</option>
                  <option value="lesson">Private Lesson</option>
                  <option value="other">Something Else</option>
                </select>
              </div>
              <div>
                <label htmlFor="ar-date" className="overline block mb-2">Event Date</label>
                <input id="ar-date" data-testid="ar-date" type="date" value={form.event_date} onChange={onChange("event_date")}
                  className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3" />
              </div>
            </div>

            <div>
              <label htmlFor="ar-location" className="overline block mb-2">Location (optional)</label>
              <input id="ar-location" data-testid="ar-location" type="text" value={form.location} onChange={onChange("location")}
                placeholder="City, venue, or 'studio'"
                className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3" />
            </div>

            <div>
              <label htmlFor="ar-message" className="overline block mb-2">Tell us about your vision</label>
              <textarea id="ar-message" data-testid="ar-message" required rows={5} value={form.message} onChange={onChange("message")}
                placeholder="Wedding party of 4, outdoor ceremony, soft natural look…"
                className="w-full text-sm bg-[#F9F7F3] border border-[#E5E0D8] focus:border-[#8C6A5D] outline-none px-3 py-3 leading-relaxed" />
            </div>

            {error && <p data-testid="artistry-error" className="text-sm text-red-700">{error}</p>}

            <button
              data-testid="artistry-submit"
              type="submit"
              disabled={submitting}
              className="w-full text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] disabled:bg-stone-300 text-white px-7 py-4 transition-colors"
            >
              {submitting ? "Sending…" : "Send Inquiry"}
            </button>
            <p className="text-xs text-stone-500 leading-relaxed">
              By submitting, you agree to be contacted by BrushBasics about your booking. We don&apos;t share your information.
            </p>
          </form>
        )}
      </section>
    </div>
  );
}
